import { Dispatch, FC, SetStateAction, useRef } from "react";
import { Dropzone as MantineDropzone, DropzoneProps as MantineDropzoneProps, FileWithPath } from "@mantine/dropzone";
import { useTranslation } from "react-i18next";
import Group from "@/components/atoms/Group";
import Text from "@/components/atoms/Text";
import CTA from "@/components/atoms/CTA";
import DocumentList from "../DocumentList";
import ImageCarousel from "../ImageCarousel";
import styles from "./styles.module.css";
import Stack from "@/components/atoms/Stack";
import { ArchivedDocumentWithDetails, ContractAttachment, DocumentBase64 } from "@/types";
import { Loader } from "@mantine/core";

const maxFileSize = 10 * 1024 ** 2; // first number decides the MB

// Mantine defaults to the FS Access API, which silently drops selections from
// inside modal portals — onDrop never fires after a successful pick. Force the
// traditional <input type="file"> flow instead.
const useFsAccessApi = false;

// Internally-owned Mantine props are removed from what the consumer can pass —
// they'd just collide with the component's own wiring otherwise.
type ForwardedMantineProps = Partial<
  Omit<MantineDropzoneProps, "onDrop" | "openRef" | "maxSize" | "children" | "useFsAccessApi">
>;

interface BaseCustomProps extends ForwardedMantineProps {
  type: "dropzone" | "button";
  onRequestRemoveFile?: (fileName: string) => void;
  buttonLabel?: string;
  showAsImageCarousel?: boolean;
  buttonPosition?: "left" | "right";
  imageCarouselHeight?: number;
  maxNumberOfFiles?: number;
  isLoading?: boolean;
  loadingInitial?: boolean;
}

type ApiFiles = ArchivedDocumentWithDetails[] | ContractAttachment[] | DocumentBase64[];

type DropzoneProps =
  | (BaseCustomProps & {
      origin: "local";
      files: File[];
      setFiles: Dispatch<SetStateAction<File[]>>;
    })
  | (BaseCustomProps & {
      origin: "api";
      files: ApiFiles;
      handleAdd: (files: File[]) => Promise<void> | void;
      handleDelete: (id: string) => Promise<unknown> | void;
    });

const Dropzone: FC<DropzoneProps> = (props) => {
  if (props.loadingInitial) return <Loader />;
  if (props.origin === "local") return <LocalDropzone {...props} />;
  return <ApiDropzone {...props} />;
};

// ---------------------------------------------------------------------------
// Shared subcomponents
// ---------------------------------------------------------------------------

const EmptyDropzoneCTA: FC<{
  onOpen: () => void;
  isLoading?: boolean;
  disabled?: boolean;
}> = ({ onOpen, isLoading, disabled }) => {
  const { t } = useTranslation();
  return (
    <Stack className={styles.dropzoneRoot} gap="20px" align="center">
      <Text size="md">{t("common:dragAndDrop")}</Text>
      <Text size="md" color="secondary">
        {t("common:or")}
      </Text>
      <CTA loading={isLoading} disabled={disabled} onClick={onOpen}>
        {t("actions:selectFiles")}
      </CTA>
      <Text size="md">{t("common:maxSize")}</Text>
    </Stack>
  );
};

const FilePreview: FC<{
  origin: "local" | "api";
  files: File[] | ApiFiles;
  showAsImageCarousel?: boolean;
  imageCarouselHeight?: number;
  onRemove: (id: string) => void;
}> = ({ origin, files, showAsImageCarousel, imageCarouselHeight, onRemove }) => {
  if (showAsImageCarousel) {
    return <ImageCarousel images={files} height={imageCarouselHeight} imagesPrSlide={3} onRemoveImage={onRemove} />;
  }
  return <DocumentList origin={origin} files={files} onRemoveFile={onRemove} />;
};

// ---------------------------------------------------------------------------
// LocalDropzone — files held in component state by the caller (File[])
// ---------------------------------------------------------------------------

interface LocalDropzoneProps extends BaseCustomProps {
  files: File[];
  setFiles: Dispatch<SetStateAction<File[]>>;
}

const LocalDropzone: FC<LocalDropzoneProps> = ({
  type,
  files,
  setFiles,
  onRequestRemoveFile,
  buttonLabel,
  showAsImageCarousel,
  buttonPosition,
  imageCarouselHeight,
  maxNumberOfFiles,
  isLoading = false,
  ...dropZoneProps
}) => {
  const { t } = useTranslation();
  const openRef = useRef<() => void>(null);

  const handleRemoveFile = (fileName: string) => {
    if (onRequestRemoveFile) {
      onRequestRemoveFile(fileName);
      return;
    }
    setFiles(files.filter((file) => file.name !== fileName));
  };

  const handleAddFiles = (newFiles: FileWithPath[]) => {
    setFiles([...files, ...newFiles]);
  };

  if (type === "dropzone") {
    return (
      <>
        <MantineDropzone
          {...dropZoneProps}
          onDrop={handleAddFiles}
          maxSize={maxFileSize}
          useFsAccessApi={useFsAccessApi}
        >
          <EmptyDropzoneCTA onOpen={() => openRef.current?.()} isLoading={isLoading} />
        </MantineDropzone>
        {files.length > 0 && (
          <Stack mt={10}>
            <FilePreview
              origin="local"
              files={files}
              showAsImageCarousel={showAsImageCarousel}
              imageCarouselHeight={imageCarouselHeight}
              onRemove={handleRemoveFile}
            />
          </Stack>
        )}
      </>
    );
  }

  const buttonDisabled = maxNumberOfFiles ? files.length >= maxNumberOfFiles : dropZoneProps.disabled;

  return (
    <MantineDropzone
      {...dropZoneProps}
      openRef={openRef}
      maxSize={maxFileSize}
      onDrop={handleAddFiles}
      activateOnClick={false}
      useFsAccessApi={useFsAccessApi}
    >
      <FilePreview
        origin="local"
        files={files}
        showAsImageCarousel={showAsImageCarousel}
        imageCarouselHeight={imageCarouselHeight}
        onRemove={handleRemoveFile}
      />
      <Group justify={buttonPosition === "left" ? "flex-start" : "flex-end"}>
        <CTA intent="secondary" onClick={() => openRef.current?.()} disabled={buttonDisabled} loading={isLoading}>
          {buttonLabel ?? t("actions:selectFiles")}
        </CTA>
      </Group>
    </MantineDropzone>
  );
};

// ---------------------------------------------------------------------------
// ApiDropzone — files served from the backend; consumer owns the mutations
// ---------------------------------------------------------------------------

interface ApiDropzoneProps extends BaseCustomProps {
  files: ApiFiles;
  handleAdd: (files: File[]) => Promise<void> | void;
  handleDelete: (id: string) => Promise<unknown> | void;
}

const ApiDropzone: FC<ApiDropzoneProps> = ({
  type,
  files,
  buttonLabel,
  showAsImageCarousel,
  buttonPosition,
  imageCarouselHeight,
  maxNumberOfFiles,
  handleAdd,
  handleDelete,
  isLoading = false,
  ...dropZoneProps
}) => {
  const { t } = useTranslation();
  const openRef = useRef<() => void>(null);

  if (type === "dropzone") {
    return (
      <MantineDropzone
        {...dropZoneProps}
        openRef={openRef}
        maxSize={maxFileSize}
        activateOnClick={false}
        onDrop={handleAdd}
        useFsAccessApi={useFsAccessApi}
      >
        {!files || files.length === 0 ? (
          <EmptyDropzoneCTA
            onOpen={() => openRef.current?.()}
            isLoading={isLoading}
            disabled={dropZoneProps.disabled}
          />
        ) : (
          <FilePreview
            origin="api"
            files={files}
            showAsImageCarousel={showAsImageCarousel}
            imageCarouselHeight={imageCarouselHeight}
            onRemove={handleDelete}
          />
        )}
      </MantineDropzone>
    );
  }

  const buttonDisabled = maxNumberOfFiles ? files.length >= maxNumberOfFiles : dropZoneProps.disabled;

  return (
    <MantineDropzone
      {...dropZoneProps}
      openRef={openRef}
      maxSize={maxFileSize}
      activateOnClick={false}
      onDrop={handleAdd}
      useFsAccessApi={useFsAccessApi}
    >
      <FilePreview
        origin="api"
        files={files}
        showAsImageCarousel={showAsImageCarousel}
        imageCarouselHeight={imageCarouselHeight}
        onRemove={handleDelete}
      />
      <Group justify={buttonPosition === "left" ? "flex-start" : "flex-end"}>
        <CTA intent="secondary" onClick={() => openRef.current?.()} disabled={buttonDisabled} loading={isLoading}>
          {buttonLabel ?? t("actions:selectFiles")}
        </CTA>
      </Group>
    </MantineDropzone>
  );
};

export default Dropzone;
